import React, { useEffect, useRef, useState } from "react";

import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import LabelIcon from "@mui/icons-material/Label";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import { Box, CircularProgress, IconButton } from "@mui/material";
import Button from "@mui/material/Button";

import { ButtonVariant } from "Constant/button";
import { routes } from "Constant/routes";
import "pages/Trackers/trackerEvents.css";
import { useLocation, useNavigate } from "react-router-dom";

import { deleteTrackerEventsAsync } from "helpers/trackers/deleteTrackerEvent";

import EmptyState from "components/atoms/EmptyState";
import PageContainer from "components/atoms/PageContainer";
import StandartButton from "components/atoms/StandartButton";
import { GenericModal } from "components/GenericModal";
import { ConfirmationModal } from "components/organisms/ConfirmationModal";
import SwalAlertComp from "components/SwalAlert";

import { TrackerEventsListProps } from "./TrackerEventsList.types";

const TrackerEventsList = ({
    language,
    trackerEvents,
    isLoadingTrackerEvents,
    systemFields,
    filtersCatalog,
    dispatchGetTrackerEvents,
    dispatchGetEventsCatalog,
    dispatchGetSystemFields,
}: TrackerEventsListProps) => {
    const location = useLocation();
    const navigate = useNavigate();

    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState<any>(null);
    const [focusedMappingIds, setFocusedMappingIds] = useState([]);
    const [focusAnimation, setFocusAnimation] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingId, setPendingId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const firstFocusedItemRef = useRef<any>(null);

    useEffect(() => {
        dispatchGetTrackerEvents();
        dispatchGetEventsCatalog();
        dispatchGetSystemFields();
    }, [
        dispatchGetTrackerEvents,
        dispatchGetEventsCatalog,
        dispatchGetSystemFields,
    ]);

    useEffect(() => {
        if (location.state?.eventMappings && trackerEvents.length > 0) {
            if (location.state?.eventMappings) {
                setFocusAnimation(true);
                setFocusedMappingIds(location.state?.eventMappings);
                setTimeout(() => {
                    setFocusAnimation(false);
                    setFocusedMappingIds([]);
                }, 2000);
            }
        }
    }, [location.state, trackerEvents]);

    useEffect(() => {
        if (focusedMappingIds.length > 0 && focusAnimation) {
            setTimeout(() => {
                if (firstFocusedItemRef.current) {
                    firstFocusedItemRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                }
            }, 100);
        }
    }, [focusedMappingIds, focusAnimation]);

    const handleShowFilters = (eventFilters: any) => {
        setSelectedFilters(eventFilters);
        setShowFiltersModal(true);
    };

    const handleCloseFiltersModal = () => {
        setShowFiltersModal(false);
        setSelectedFilters(null);
    };

    const deleteEvents = async (id: string) => {
        setDeletingId(id);

        try {
            const response = await deleteTrackerEventsAsync(id);
            if (response?.status) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: response?.message,
                    icon: "success",
                    showClass: {
                        popup: "animate_animated animate_fadeInDown",
                    },
                    hideClass: {
                        popup: "animate_animated animate_fadeOutUp",
                    },
                    timer: 3000,
                });
                dispatchGetTrackerEvents();
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response?.message,
                    icon: "error",
                    showClass: {
                        popup: "animate_animated animate_fadeInDown",
                    },
                    hideClass: {
                        popup: "animate_animated animate_fadeOutUp",
                    },
                });
            }
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <PageContainer
            title={language?.tracker_event?.title ?? "Tracker Events"}
            subtitle={
                language?.tracker_event?.desc ??
                "On this page, you can view tracker events, and create a new one by using the button."
            }
            additionalButtons={
                <Button
                    color="warning"
                    onClick={() => navigate(routes.tracker_events_create)}
                    variant="contained"
                >
                    {language?.buttons?.add_event_mapping ??
                        "Add Event Mapping"}
                </Button>
            }
            loading={isLoadingTrackerEvents}
            variant="list"
        >
            <ConfirmationModal
                open={confirmOpen}
                onClose={() => {
                    setConfirmOpen(false);
                    setPendingId(null);
                }}
                onConfirm={() => {
                    const id = pendingId;
                    setConfirmOpen(false);
                    setPendingId(null);
                    if (id) {
                        deleteEvents(id);
                    }
                }}
                title={
                    language?.delete_swal_modal?.are_you_sure ?? "Are you sure?"
                }
                message="This action cannot be undone."
                confirmColor="error"
            />
            {!trackerEvents?.length ? (
                <EmptyState
                    icon={<LabelOutlinedIcon />}
                    title={
                        language?.tracker_event?.no_events_yet ??
                        "No event mappings yet"
                    }
                    description={
                        language?.tracker_event?.no_events_yet_desc ??
                        "Create your first event mapping to start tracking custom events for this tracker."
                    }
                />
            ) : (
                <div>
                    {trackerEvents?.map((item, index) => {
                        const isFocused =
                            focusedMappingIds.find(
                                (mapping: any) =>
                                    mapping.source_tracker?.id ===
                                    item.source_tracker?.id,
                            ) && focusAnimation;

                        const firstFocusedIndex = trackerEvents.findIndex(
                            (eventItem: any) =>
                                focusedMappingIds.find(
                                    (mapping: any) =>
                                        mapping.source_tracker?.id ===
                                        eventItem.source_tracker?.id,
                                ) && focusAnimation,
                        );
                        const isFirstFocused =
                            isFocused && index === firstFocusedIndex;

                        return (
                            <div
                                ref={
                                    isFirstFocused ? firstFocusedItemRef : null
                                }
                                className={`event-item ${
                                    index > 0 ? "mt-3" : ""
                                } ${isFocused ? "focus-animation" : ""}`}
                                style={{
                                    ...(isFocused
                                        ? {
                                              animation:
                                                  "focusPulse 2s ease-in-out",
                                              border: "1px solid #6c757d",
                                              borderRadius: "8px",
                                              boxShadow:
                                                  "0 0 20px rgba(108, 117, 125, 0.3)",
                                              transform: "scale(1.02)",
                                              transition:
                                                  "all 0.3s ease-in-out",
                                          }
                                        : {}),
                                }}
                            >
                                <div className="event-item-body">
                                    <div className="row justify-between">
                                        <div className="col-md-3 col-sm-12">
                                            <p
                                                className="pb-2 trackerEventTitle"
                                                style={{ opacity: ".5" }}
                                            >
                                                {language?.tracker_event
                                                    ?.source_tracker ??
                                                    "Source Tracker"}
                                            </p>
                                            <p className="trackerEventTitle">
                                                {
                                                    item?.source_tracker
                                                        ?.tracker_name
                                                }
                                            </p>
                                        </div>
                                        <div className="col-md-3 col-sm-12">
                                            <p
                                                className="pb-2 trackerEventTitle"
                                                style={{ opacity: ".5" }}
                                            >
                                                {language?.tracker_event
                                                    ?.destination_tracker ??
                                                    "Destination Tracker"}
                                            </p>
                                            <p className="trackerEventTitle">
                                                {
                                                    item?.destination_tracker
                                                        ?.tracker_name
                                                }
                                            </p>
                                        </div>
                                    </div>
                                    {item?.events &&
                                        item?.events.length > 0 &&
                                        item?.events.map((i: any) => (
                                            <div className="row justify-between mt-3 event-item-el">
                                                <div className="col-md-3 col-sm-12">
                                                    <span className="event-item-code">
                                                        {i?.source_event?.code}
                                                    </span>
                                                    <div className="event-item-prefix-area">
                                                        {((i?.prefix !== "" &&
                                                            i?.prefix !==
                                                                null) ||
                                                            (i?.suffix !== "" &&
                                                                i?.suffix !==
                                                                    null)) && (
                                                            <>
                                                                {i?.prefix !==
                                                                    "" &&
                                                                    i?.prefix !==
                                                                        null && (
                                                                        <span className="event-item-prefix">
                                                                            <strong>
                                                                                Prefix:
                                                                            </strong>{" "}
                                                                            {
                                                                                i?.prefix
                                                                            }
                                                                        </span>
                                                                    )}
                                                                {i?.suffix !==
                                                                    "" &&
                                                                    i?.suffix !==
                                                                        null && (
                                                                        <span className="event-item-prefix">
                                                                            <strong>
                                                                                Suffix:
                                                                            </strong>{" "}
                                                                            {
                                                                                i?.suffix
                                                                            }
                                                                        </span>
                                                                    )}
                                                            </>
                                                        )}
                                                        {i?.type !== "" &&
                                                            i?.type !==
                                                                null && (
                                                                <span className="event-item-prefix">
                                                                    <strong>
                                                                        Type:
                                                                    </strong>{" "}
                                                                    {i?.type}
                                                                </span>
                                                            )}
                                                        {i?.filters?.conditions
                                                            ?.length > 0 && (
                                                            <span
                                                                className="event-item-prefix clickable-filters"
                                                                onClick={() =>
                                                                    handleShowFilters(
                                                                        i.filters,
                                                                    )
                                                                }
                                                                title="Click to view filters"
                                                            >
                                                                <strong>
                                                                    Filters:
                                                                </strong>{" "}
                                                                {
                                                                    i?.filters
                                                                        ?.conditions
                                                                        ?.length
                                                                }
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-md-6 col-sm-12">
                                                    <hr></hr>
                                                </div>
                                                <div className="col-md-3 col-sm-12 d-flex justify-between items-center gap-2">
                                                    <span className="event-item-code">
                                                        {
                                                            i?.destination_event
                                                                ?.code
                                                        }
                                                    </span>
                                                    <div className="d-flex gap-2">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() =>
                                                                navigate(
                                                                    `${routes.tracker_events_edit}/${i.id}`,
                                                                )
                                                            }
                                                            title="Edit Event"
                                                        >
                                                            <EditIcon />
                                                        </IconButton>{" "}
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => {
                                                                setPendingId(
                                                                    i?.id,
                                                                );
                                                                setConfirmOpen(
                                                                    true,
                                                                );
                                                            }}
                                                            disabled={
                                                                deletingId !==
                                                                null
                                                            }
                                                            title="Delete Event"
                                                        >
                                                            {deletingId ===
                                                            i?.id ? (
                                                                <CircularProgress
                                                                    color="inherit"
                                                                    size={20}
                                                                />
                                                            ) : (
                                                                <DeleteIcon />
                                                            )}
                                                        </IconButton>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Filters Modal */}
            <GenericModal
                open={showFiltersModal}
                onClose={handleCloseFiltersModal}
                title="Event Filters"
                maxWidth="md"
                content={
                    selectedFilters ? (
                        <Box
                            className="filters-details"
                            sx={{ p: { xs: 1.25, sm: 1.875, md: 2.5 } }}
                        >
                            {selectedFilters?.logical_operator && (
                                <div className="filters-summary mb-4">
                                    <div className="summary-icon">
                                        <SettingsIcon />
                                    </div>
                                    <div className="summary-content">
                                        <strong>Logical Operator:</strong>
                                        <span className="operator-value">
                                            {selectedFilters?.logical_operator ||
                                                "N/A"}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {selectedFilters?.conditions &&
                            selectedFilters.conditions.length > 0 ? (
                                <div className="filters-grid">
                                    <div className="filters-grid-header">
                                        <h6 className="mb-0">
                                            Applied Filters (
                                            {selectedFilters.conditions.length})
                                        </h6>
                                    </div>
                                    <div className="filters-grid-container">
                                        {[...selectedFilters.conditions].map(
                                            (condition: any, index: number) => (
                                                <div
                                                    key={index}
                                                    className="filter-card"
                                                >
                                                    <div className="filter-card-body">
                                                        <div className="filter-field-row">
                                                            <div className="filter-label">
                                                                <LabelIcon className="me-2" />
                                                                Field
                                                            </div>
                                                            <div className="filter-value-text">
                                                                {(() => {
                                                                    const fieldLabel =
                                                                        systemFields[
                                                                            condition
                                                                                .field
                                                                        ];
                                                                    return (
                                                                        fieldLabel ||
                                                                        condition.field ||
                                                                        "N/A"
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                        <div className="filter-field-row">
                                                            <div className="filter-label">
                                                                <CheckIcon className="me-2" />
                                                                Condition
                                                            </div>
                                                            <div className="filter-value-text">
                                                                {(() => {
                                                                    const conditionFilter =
                                                                        filtersCatalog?.find(
                                                                            (
                                                                                filter: any,
                                                                            ) =>
                                                                                filter.code ===
                                                                                condition.condition,
                                                                        );
                                                                    return conditionFilter
                                                                        ? conditionFilter.name
                                                                        : condition.condition ||
                                                                              "N/A";
                                                                })()}
                                                            </div>
                                                        </div>
                                                        {condition.value && (
                                                            <div className="filter-field-row">
                                                                <div className="filter-label">
                                                                    <SearchIcon className="me-2" />
                                                                    Value
                                                                </div>
                                                                <div className="filter-value-text">
                                                                    {condition.value ||
                                                                        "N/A"}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ),
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="no-filters-message">
                                    <div className="no-filters-icon">
                                        <InfoIcon />
                                    </div>
                                    <div className="no-filters-text">
                                        <h6>No Filters Applied</h6>
                                        <p>
                                            Currently showing all events without
                                            any filtering criteria.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </Box>
                    ) : null
                }
                footer={
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                        }}
                    >
                        <StandartButton
                            onClick={handleCloseFiltersModal}
                            variant={ButtonVariant.BLACK_OUTLINED}
                        >
                            {language?.buttons?.close ?? "Close"}
                        </StandartButton>
                    </Box>
                }
            />
        </PageContainer>
    );
};

export default TrackerEventsList;
